import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import { DatePickerNew, Input, Select, TextArea } from 'common-src/components/base';
import {
  AssessmentTypeOptions,
  CaregiverStatus,
  CaregiverStatusOptions,
  ClinicianAttestationOptions,
  EligibleResidenceAttestationOptions,
  isRccPartnerIdAllowed,
  NursingHomeStatusOptions,
  ReAssessmentReasonOptions,
  ResidenceTypeOptions,
  validatePatientGuideCmsAlignment,
} from 'common-src/models/PatientGuideCmsAlignment';
import { ColorsNew } from 'common-src/styles';

import styles from '../../AddAlignmentPopup.module.scss';

const ReAssessment = ({ data, handleInputChange, errors, setErrors }) => (
  <>
    <Select
      id="assessment-type"
      label="Assessment type"
      options={AssessmentTypeOptions}
      onChange={(op) => handleInputChange('assessmentType', op.value)}
      value={AssessmentTypeOptions.find((op) => op.value === data.assessmentType)}
      required
      requiredColor={ColorsNew.mediumDarkRed}
    />
    <DatePickerNew
      id="assessment-date"
      header="Assessment date"
      placeholder="Select date"
      value={data.assessmentDate}
      onDateSelected={(date) =>
        handleInputChange('assessmentDate', date ? moment(date).format('YYYY-MM-DD') : null)
      }
      onBlur={(date) =>
        setErrors((prev) => ({
          ...prev,
          ...validatePatientGuideCmsAlignment('assessmentDate', date),
        }))
      }
      errorText={errors.assessmentDate}
      required
      requiredColor={ColorsNew.mediumDarkRed}
      maxDate={new Date()}
    />
    <Select
      id="re-assessment-reason"
      label="Re-assessment reason"
      placeholder="Select"
      options={ReAssessmentReasonOptions}
      onChange={(op) => handleInputChange('reassessmentReason', op.value)}
      value={ReAssessmentReasonOptions.find((op) => op.value === data.reassessmentReason)}
      required
      requiredColor={ColorsNew.mediumDarkRed}
    />
    <Select
      id="caregiver-status"
      label="Caregiver status"
      placeholder="Select"
      options={CaregiverStatusOptions}
      onChange={(op) => {
        handleInputChange('caregiverStatus', op.value);

        if (op.value !== CaregiverStatus.Other) {
          handleInputChange('caregiverStatusOs', null);
        }
      }}
      value={CaregiverStatusOptions.find((op) => op.value === data.caregiverStatus)}
      required
      requiredColor={ColorsNew.mediumDarkRed}
    />
    <Select
      id="clinician-attestation"
      label="Clinician attestation"
      placeholder="Select"
      options={ClinicianAttestationOptions}
      onChange={(op) => handleInputChange('clinicianAttestation', op.value)}
      value={ClinicianAttestationOptions.find((op) => op.value === data.clinicianAttestation)}
      required
      requiredColor={ColorsNew.mediumDarkRed}
    />
    <Select
      id="nursing-home-status"
      label="Nursing home status"
      placeholder="Select"
      options={NursingHomeStatusOptions}
      onChange={(op) => handleInputChange('nursingHomeStatus', op.value)}
      value={NursingHomeStatusOptions.find((op) => op.value === data.nursingHomeStatus)}
      required
      requiredColor={ColorsNew.mediumDarkRed}
    />
    <Select
      id="eligible-residence-attestation"
      label="Residence attestation"
      placeholder="Select"
      options={EligibleResidenceAttestationOptions}
      onChange={(op) => handleInputChange('eligibleResidenceAttestation', op.value)}
      value={EligibleResidenceAttestationOptions.find(
        (op) => op.value === data.eligibleResidenceAttestation,
      )}
    />
    {data.caregiverStatus === CaregiverStatus.Other && (
      <TextArea
        id="caregiver-status-specify"
        classNames={[styles.width100]}
        placeholder="Text"
        value={data.caregiverStatusOs ?? ''}
        onTextChange={(value) => handleInputChange('caregiverStatusOs', value)}
        fixedRows={3}
        label="Caregiver status, please specify"
        required
        requiredColor={ColorsNew.mediumDarkRed}
      />
    )}
    <Select
      id="residence-type"
      label="Residence type"
      placeholder="Select"
      options={ResidenceTypeOptions}
      onChange={(op) => handleInputChange('residenceType', op.value)}
      value={ResidenceTypeOptions.find((op) => op.value === data.residenceType)}
      required
      requiredColor={ColorsNew.mediumDarkRed}
    />
    {isRccPartnerIdAllowed(data.residenceType) && (
      <Input
        id="rcc-partner-id"
        placeholder="5-digit ID"
        label="RCC Partner ID"
        value={data.rccPartnerId ?? ''}
        onTextChange={(value) =>
          handleInputChange('rccPartnerId', value.replace(/\D/g, '').slice(0, 5))
        }
        errorText={errors.rccPartnerId}
        tooltipText="5-digit RCC Partner ID from the Salesforce Participant Portal"
      />
    )}
    <Input
      id="npi"
      placeholder="NPI"
      label="NPI"
      value={data.npi ?? ''}
      onTextChange={(value) => handleInputChange('npi', value)}
      required
      requiredColor={ColorsNew.mediumDarkRed}
      errorText={errors.npi}
      tooltipText="Attesting (diagnosing) provider NPI"
    />
    <div className={styles.fieldRow}>
      <Input
        id="clinician-first-name"
        placeholder="First name"
        label="Clinician first name"
        value={data.clinicianFirstName ?? ''}
        onTextChange={(value) => handleInputChange('clinicianFirstName', value)}
        required
        requiredColor={ColorsNew.mediumDarkRed}
        errorText={errors.clinicianFirstName}
        tooltipText="Attesting (diagnosing) provider first name"
      />
      <Input
        id="clinician-middle-name"
        placeholder="Middle name"
        label="Clinician middle name"
        value={data.clinicianMiddleName ?? ''}
        onTextChange={(value) => handleInputChange('clinicianMiddleName', value)}
        errorText={errors.clinicianMiddleName}
        tooltipText="Attesting (diagnosing) provider middle name"
      />
      <Input
        id="clinician-last-name"
        placeholder="Last name"
        label="Clinician last name"
        value={data.clinicianLastName ?? ''}
        onTextChange={(value) => handleInputChange('clinicianLastName', value)}
        required
        requiredColor={ColorsNew.mediumDarkRed}
        errorText={errors.clinicianLastName}
        tooltipText="Attesting (diagnosing) provider last name"
      />
    </div>
  </>
);

ReAssessment.propTypes = {
  data: PropTypes.object,
  handleInputChange: PropTypes.func,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
};

export default ReAssessment;
